import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { supportedSubfolderLocaleCodes } from '@coursera-web/internationalization-lib';

import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import type {
  ArticlesDirectoryBySubfolderContentQuery,
  ArticlesDirectoryBySubfolderContentQueryVariables,
} from 'bundles/directory-graphql/__generated__/contentfulTypes';
import { itemsPerPage } from 'bundles/directory-graphql/common/constants';
import { generateHrefWithPreview } from 'bundles/directory-graphql/common/utils';
import { articlesDirectoryBySubfolderContentQuery } from 'bundles/directory-graphql/queries/ArticlesDirectoryBySubfolderContentQuery';

type ArticleDirectoryItem = {
  title: string;
  slug: string;
};

export const useArticlesSubfolderData = ({
  localeCode,
  currentPage,
  isPreview = false,
}: {
  localeCode?: string;
  currentPage?: number;
  isPreview?: boolean;
}) => {
  let languageCode = 'en';
  let countryCode = 'us';
  if (localeCode) {
    // try to parse the localeCode
    const [languageCodeFromProp, countryCodeFromProp] = localeCode.split('-');

    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    languageCode = languageCodeFromProp;

    if (countryCodeFromProp) {
      countryCode = countryCodeFromProp.toLowerCase();
    }
  }

  const start = currentPage ? (currentPage - 1) * itemsPerPage : 0;

  const { data } = useQuery<
    ArticlesDirectoryBySubfolderContentQuery,
    ArticlesDirectoryBySubfolderContentQueryVariables
  >(articlesDirectoryBySubfolderContentQuery, {
    variables: {
      languageCode,
      countryCode,
      limit: itemsPerPage,
      skip: start,
      isPreview,
    },
    context: getGrowthCmsGraphqlContext(isPreview),
    errorPolicy: 'all',
  });

  const subfolderLocaleCode = supportedSubfolderLocaleCodes.includes(localeCode || '') ? `${localeCode}/` : '';

  const articles = useMemo(
    () =>
      data?.articleCollection?.items
        .filter((i): i is ArticleDirectoryItem => !!i?.slug && !!i?.title)
        ?.map((i) => ({
          ...i,
          link: generateHrefWithPreview(`${subfolderLocaleCode}articles/${i.slug}`, isPreview),
        })) ?? [],
    [data, subfolderLocaleCode, isPreview]
  );

  const total = data?.articleCollection?.total;

  return {
    articles,
    total,
  };
};
